import { UserOutcomeDetail } from '@app/outcomes/outcomes-api.model';
import { EventEmitter, Injectable } from '@angular/core';
import { UserPosition } from '@app/inputs/inputs-api.model';
import { UserOutcomeModalService } from '@app/outcomes/services/user-outcome-modal.service';
import {
  ModalService,
  NotifierService,
  TrackerService,
} from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { SimpleModalComponent } from '@app/shared/components/modal/simple-modal/simple-modal.component';
import { DeleteModalPayload } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { InputsService } from '@app/inputs/services/inputs.service';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { catchAndSurfaceError } from '@app/shared/utils';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { ProfileCollectionItem } from '@app/profile/profile-api.model';

@Injectable({
  providedIn: 'root',
})
export class UserOutcomeActionsService {
  public i18n = this.translateService.instant([
    'Core_YesSure',
    'UserProfileSvc_ProblemDeletingCollectionItem',
  ]);

  private _itemSelected = new Subject<
    ProfileCollectionItem<UserOutcomeDetail>
  >();

  public readonly itemSelected = this._itemSelected.asObservable();

  constructor(
    private userOutcomeModalService: UserOutcomeModalService,
    private notifierService: NotifierService,
    private translateService: TranslateService,
    private modalService: ModalService,
    private inputsService: InputsService,
    private http: NgxHttpClient,
    private trackerService: TrackerService
  ) {}

  public notifyItemSelected(item: ProfileCollectionItem<any>) {
    this._itemSelected.next(item);
  }

  /**
   * Show the edit modal for a userOutcome - award, degree, accomplishment, certificate
   * @param event - click event from the menu
   * @param userOutcome - the award, accomplishment, degree or certificate to edit
   * @param eventEmitter - eventEmitter to emit when the userOutcome has been successfully edited
   */
  public editUserOutcome(
    event: Event,
    userOutcome: UserOutcomeDetail,
    eventEmitter: EventEmitter<UserOutcomeDetail | UserPosition>
  ): void {
    this.userOutcomeModalService
      .edit({
        outcomeType: userOutcome.contentTypeId,
        userOutcomeId: userOutcome.userOutcomeId,
        sourceTarget: event.target,
      })
      .subscribe({
        next: () => {
          eventEmitter.emit(userOutcome);
          this.notifierService.showSuccess(
            this.translateService.instant(
              'ProfileCtrl_EditCollectionItemNotifierFormat',
              {
                itemTitle:
                  userOutcome.contentType === 'Badge' && !userOutcome?.title
                    ? 'Badge'
                    : userOutcome.title,
              }
            )
          );
        },
        error: (e) => {
          if (e) {
            console.error(e);
          }
        },
      });
  }

  /**
   * Show the delete modal for a userOutcome / experience
   * @param headerText - The header text for the modal
   * @param bodyText - The body text for the modal
   * @param payload - The full user outcome or experience to delete
   * @param title - The item title
   * @param eventEmitter - eventEmitter to emit when the item has been successfully deleted
   */
  // used in UserOutcomeActions and UserExperienceActions
  public showDeleteModal(
    headerText: string,
    bodyText: string,
    payload: DeleteModalPayload,
    title: string,
    eventEmitter?: EventEmitter<UserOutcomeDetail | UserPosition>
  ): void {
    // use setTimeout to prevent modal focus being lost
    setTimeout(() =>
      this.modalService
        .show<void>(SimpleModalComponent, {
          inputs: {
            canCancel: true,
            bodyClasses: 'center-text',
            bodyText,
            headerText,
            submitButtonText: this.i18n.Core_YesSure,
          },
        })
        .subscribe(() => {
          this.deleteCollectionItem(payload).subscribe(() => {
            eventEmitter?.emit();
            this.inputsService.notifyInputModified(payload.contentType);
            this.notifierService.showSuccess(
              this.translateService.instant(
                'ProfileCtrl_DeleteCollectionItemNotifierFormat',
                {
                  itemTitle: title,
                }
              )
            );
          });
        })
    );
  }

  // used in UserOutcomeTile only
  public deleteCollectionItem(item): Observable<void> {
    return this.http.post('/userprofile/deletecollectionitem', item).pipe(
      map(() => {
        this.trackerService.trackEventData({
          action: 'User Item Deleted',
          category: item.contentType,
        });
      }),
      catchAndSurfaceError(
        this.i18n.UserProfileSvc_ProblemDeletingCollectionItem
      )
    );
  }
}
